import React, { useMemo, useEffect } from 'react';
import CampaignCard from './CampaignCard';
import {
	PaperAirplaneIcon,
	ShoppingCartIcon,
	UserAddIcon,
	ClockIcon,
	TrashIcon,
} from '@heroicons/react/outline';
import { useCampaigns, useUpdateCampaigns } from '../../hooks/useEngageApi';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Campaign type mapping with icons and descriptions
const CAMPAIGN_CONFIG = {
	WELCOME_USER: {
		title: 'Welcome New Users',
		description: 'Make a strong first impression with a welcome message that sets the tone and builds early engagement with new users.',
		icon: <PaperAirplaneIcon className="w-5 h-5 text-blue-500" />,
	},
	ABANDONED_CART: {
		title: 'Abandoned Cart',
		description: 'Recover lost sales by reminding users of items they left behind, turning missed opportunities into conversions.',
		icon: <ShoppingCartIcon className="w-5 h-5 text-blue-500" />,
	},
	REMOVED_FROM_CART: {
		title: 'Removed from Cart',
		description: 'Reignite interest by gently nudging users to reconsider the items they removed from their cart, driving potential sales.',
		icon: <TrashIcon className="w-5 h-5 text-blue-500" />,
	},
	ABANDONED_CHECKOUT: {
		title: 'Abandoned Checkout',
		description: 'Reduce drop-offs at the final hurdle by reminding users to complete their purchase, turning intent into action.',
		icon: <ShoppingCartIcon className="w-5 h-5 text-orange-500" />,
	},
	INACTIVE_USER: {
		title: 'Inactive User',
		description: 'Re-engage users who have been inactive for a while by sending them a gentle reminder to return and explore your app.',
		icon: <UserAddIcon className="w-5 h-5 text-blue-500" />,
	},
};

const Campaign = ({ projectId }) => {
	const queryClient = useQueryClient();

	// Fetch campaigns data - always fetch fresh data, ignore cache
	const { 
		data: campaignsResponse, 
		isLoading, 
		error,
		refetch 
	} = useCampaigns(projectId, {}, {
		staleTime: 0, // Always consider data stale
		cacheTime: 0, // Don't cache data
		refetchOnWindowFocus: true, // Refetch when window gains focus
		refetchOnMount: 'always', // Always refetch on mount
		onError: (error) => {
			console.error('Failed to load campaigns:', error);
			toast.error('Failed to load campaigns');
		}
	});

	// Force refetch campaigns data whenever this component mounts
	// This ensures fresh data when user navigates to Campaigns tab
	useEffect(() => {
		if (projectId && refetch) {
			// Invalidate cache and trigger immediate refetch
			queryClient.invalidateQueries(['campaigns', projectId]);
			refetch();
		}
	}, [projectId, queryClient, refetch]);

	// Update campaign mutation
	const updateCampaignMutation = useUpdateCampaigns({
		onMutate: async (variables) => {
			console.log('onMutate: Starting optimistic update for:', variables.data.slug);
			
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries(['campaigns', projectId]);

			// Snapshot the previous value
			const queryKey = ['campaigns', projectId, '{}'];
			const previousCampaigns = queryClient.getQueryData(queryKey);

			// Optimistically update the cache
			queryClient.setQueryData(queryKey, (old) => {
				if (!old?.data?.campaigns) {
					console.log('onMutate: No existing campaign data found');
					return old;
				}

				const updatedCampaigns = old.data.campaigns.map(campaign => {
					if (campaign.slug === variables.data.slug) {
						console.log('onMutate: Updating campaign:', {
							slug: campaign.slug,
							oldSequenceCount: campaign.campaign_sequences?.length || 0,
							newSequenceCount: variables.data.sequences?.length || 0,
							oldStatus: campaign.status,
							newStatus: variables.data.status
						});
						
						return {
							...campaign,
							status: variables.data.status,
							campaign_sequences: variables.data.sequences || campaign.campaign_sequences,
							updated_at: new Date().toISOString(),
						};
					}
					return campaign;
				});

				return {
					...old,
					data: {
						...old.data,
						campaigns: updatedCampaigns,
					},
				};
			});

			// Return a context object with the snapshotted value
			return { previousCampaigns, queryKey };
		},
		onSuccess: (response) => {
			console.log('Campaign update successful:', response);
			toast.success('Campaign updated successfully!');
			
			// Invalidate and refetch to ensure we have the latest data from server
			queryClient.invalidateQueries(['campaigns', projectId]);
			
			// Also invalidate campaign insights to update status there
			queryClient.invalidateQueries(['campaignInsights', projectId]);
		},
		onError: (error, variables, context) => {
			console.error('Failed to update campaign:', error);
			console.error('Variables that failed:', variables);
			console.error('Full error response:', error.response);
			
			// Rollback the optimistic update
			if (context?.previousCampaigns && context?.queryKey) {
				console.log('onError: Rolling back optimistic update');
				queryClient.setQueryData(context.queryKey, context.previousCampaigns);
			}
			
			// Enhanced error handling to show server validation messages
			if (error.response?.data) {
				const errorData = error.response.data;
				
				// Check for specific validation errors
				if (errorData.errors && Array.isArray(errorData.errors)) {
					// Multiple validation errors
					errorData.errors.forEach(err => {
						toast.error(err.message || err);
					});
				} else if (errorData.message) {
					// Single error message
					toast.error(errorData.message);
				} else if (errorData.error) {
					// Alternative error field
					toast.error(errorData.error);
				} else {
					// Fallback for structured error responses
					toast.error('Server validation failed. Please check your inputs.');
				}
			} else if (error.response?.status === 400) {
				toast.error('Invalid campaign data. Please check your inputs and try again.');
			} else if (error.response?.status === 401) {
				toast.error('Unauthorized. Please log in again.');
			} else if (error.response?.status === 403) {
				toast.error('You do not have permission to update this campaign.');
			} else if (error.response?.status === 404) {
				toast.error('Campaign not found.');
			} else if (error.response?.status === 422) {
				toast.error('Validation failed. Please check your inputs.');
			} else if (error.response?.status === 500) {
				toast.error('Server error. Please try again later.');
			} else if (error.response?.status >= 500) {
				toast.error('Server error. Please try again later.');
			} else {
				toast.error(error.message || 'Failed to update campaign. Please try again.');
			}
		},
		onSettled: () => {
			// Always refetch after error or success to ensure consistency
			console.log('onSettled: Invalidating queries');
			queryClient.invalidateQueries(['campaigns', projectId]);
		},
	});

	// Transform API data to UI format and order according to CAMPAIGN_CONFIG
	const campaigns = useMemo(() => {
		if (!campaignsResponse?.data?.campaigns) return [];
		
		// Create a map of API campaigns by slug for easy lookup
		const campaignMap = new Map();
		campaignsResponse.data.campaigns.forEach(campaign => {
			campaignMap.set(campaign.slug, campaign);
		});

		// Order campaigns according to CAMPAIGN_CONFIG definition
		const orderedCampaigns = [];
		Object.keys(CAMPAIGN_CONFIG).forEach(slug => {
			const apiCampaign = campaignMap.get(slug);
			if (apiCampaign) {
				const config = CAMPAIGN_CONFIG[slug];
				orderedCampaigns.push({
					id: apiCampaign.id,
					slug: apiCampaign.slug,
					title: config.title,
					description: config.description,
					icon: config.icon,
					status: apiCampaign.status, // ACTIVE or PAUSED
					sequences: (apiCampaign.campaign_sequences || []).sort((a, b) => a.sequence - b.sequence),
					insights: apiCampaign.campaign_insights || [],
					created_at: apiCampaign.created_at,
					updated_at: apiCampaign.updated_at,
				});
			}
		});

		// Add any campaigns from API that are not in CAMPAIGN_CONFIG (fallback)
		campaignsResponse.data.campaigns.forEach(campaign => {
			if (!CAMPAIGN_CONFIG[campaign.slug]) {
				const config = {
					title: campaign.slug.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
					description: 'Campaign description not available',
					icon: <ClockIcon className="w-5 h-5 text-gray-500" />,
				};

				orderedCampaigns.push({
					id: campaign.id,
					slug: campaign.slug,
					title: config.title,
					description: config.description,
					icon: config.icon,
					status: campaign.status,
					sequences: (campaign.campaign_sequences || []).sort((a, b) => a.sequence - b.sequence),
					insights: campaign.campaign_insights || [],
					created_at: campaign.created_at,
					updated_at: campaign.updated_at,
				});
			}
		});

		return orderedCampaigns;
	}, [campaignsResponse]);

	// Handle campaign update
	const handleUpdateCampaign = (campaignData) => {
		console.log('handleUpdateCampaign called with:', campaignData);
		
		if (!projectId) {
			toast.error('Project ID is missing');
			return;
		}

		// Basic validation - only check for required fields
		if (!campaignData.slug) {
			toast.error('Campaign slug is required');
			return;
		}

		if (!campaignData.status) {
			toast.error('Campaign status is required');
			return;
		}

		// Basic sequence validation - only check for required fields
		if (campaignData.sequences && campaignData.sequences.length > 0) {
			const invalidSequences = campaignData.sequences.filter(seq => 
				!seq.title || !seq.message || !seq.trigger_unit || !seq.trigger_value
			);
			
			if (invalidSequences.length > 0) {
				toast.error('All sequences must have title, message, and trigger settings');
				return;
			}
		}

		console.log('About to call updateCampaignMutation.mutate with:', {
			projectId,
			data: campaignData,
		});

		updateCampaignMutation.mutate({
			projectId,
			data: campaignData,
		});
	};

	if (isLoading) {
		return (
			<div className="p-6">
				<h1 className="text-2xl font-medium mb-4">Campaigns</h1>
				<div className="flex flex-col gap-4">
					{[1, 2, 3, 4, 5].map((i) => (
						<div key={i} className="rounded-lg border border-gray-200 p-4 animate-pulse">
							<div className="flex flex-row gap-4">
								<div className="w-12 h-12 bg-gray-200 rounded-full"></div>
								<div className="flex-1 space-y-2">
									<div className="h-4 bg-gray-200 rounded w-1/3"></div>
									<div className="h-3 bg-gray-200 rounded w-1/4"></div>
									<div className="h-3 bg-gray-200 rounded w-2/3"></div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-6">
				<h1 className="text-2xl font-medium mb-4">Campaigns</h1>
				<div className="text-center py-8">
					<p className="text-red-600 mb-4">Failed to load campaigns</p>
					<button 
						onClick={() => refetch()}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
					>
						Try Again
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-medium mb-2">Campaigns</h1>
				<p className="text-sm text-gray-600">
					Manage your automated notification campaigns to engage users at key moments
				</p>
			</div>
			
			{campaigns.length === 0 ? (
				<div className="text-center py-8">
					<p className="text-gray-500 mb-4">No campaigns found</p>
					<button 
						onClick={() => refetch()}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
					>
						Refresh
					</button>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{campaigns.map((campaign) => (
						<CampaignCard 
							key={campaign.id} 
							{...campaign}
							onUpdate={handleUpdateCampaign}
							isUpdating={updateCampaignMutation.isLoading && updateCampaignMutation.variables?.data?.slug === campaign.slug}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default Campaign;
